import React, { useState } from 'react';

import { Dialog, Alert, Classes, Button, FormGroup, TextArea, Callout, Tag } from '@blueprintjs/core';

import API from '../Utilities/API';

const normalizeSearchData = (parsed) => {
    if (!parsed || typeof parsed !== 'object') return null;

    const nestedQuery = parsed.query && typeof parsed.query === 'object' && parsed.query !== null
        ? parsed.query
        : null;

    const normalized = {
        note: parsed.note ?? null,
        kwtype: nestedQuery?.kwtype ?? parsed.kwtype,
        language: nestedQuery?.language ?? parsed.language,
        country: nestedQuery?.country ?? parsed.country
    };

    const keywords = nestedQuery?.keywords ?? parsed.keywords;
    if (keywords !== undefined && keywords !== null) {
        normalized.keywords = keywords;
    }

    const search = nestedQuery?.search ?? parsed.search;
    if (search !== undefined && search !== null) {
        normalized.search = String(search);
    }

    return normalized;
};

const isSearchable = (data) => (
    data && ['keywords', 'search', 'kwtype', 'language', 'country'].some((key) => {
        const value = data[key];
        if (value === undefined || value === null) return false;
        if (Array.isArray(value)) return value.length > 0;
        return String(value).trim() !== '';
    })
);

export default ({ onHide, onSubmit, onSearch, text: initialText = '', keywords = [] }) => {
    const [text, setText] = useState(initialText || '');
    const [submitting, setSubmitting] = useState(false);
    const [responseText, setResponseText] = useState(null);
    const [error, setError] = useState(null);
    const [notePrompt, setNotePrompt] = useState(null);

    const runSearch = (normalizedData) => {
        if (isSearchable(normalizedData)) {
            if (onSearch) {
                const payload = {};
                if (normalizedData.keywords !== undefined) payload.keywords = normalizedData.keywords;
                if (normalizedData.search !== undefined) payload.search = normalizedData.search;
                if (normalizedData.kwtype !== undefined) payload.kwtype = normalizedData.kwtype;
                if (normalizedData.language !== undefined) payload.language = normalizedData.language;
                if (normalizedData.country !== undefined) payload.country = normalizedData.country;
                onSearch(payload);
            }
            onHide();
            return;
        }
        setError('No usable search parameters in the response.');
    };

    const handleSearch = () => {
        const query = text.trim();
        if (!query || submitting) return;

        const payload = { query };
        if (keywords.length > 0) {
            payload.keywords = keywords.map((keyword) => ({
                id: keyword.id,
                title: keyword.title || String(keyword.id)
            }));
        }

        setSubmitting(true);
        setResponseText(null);
        setError(null);
        setNotePrompt(null);

        API.AI.ConstructQuery(payload)
            .then((response) => {
                console.log(response);
                let parsedData = null;
                let displayText = null;

                try {
                    const data = response.data.Data;
                    if (typeof data === 'string') {
                        const parsed = JSON.parse(data);
                        parsedData = typeof parsed === 'object' && parsed !== null ? parsed : null;
                        displayText = JSON.stringify(parsed, null, 2);
                    } else if (typeof data === 'object' && data !== null) {
                        parsedData = data;
                        displayText = JSON.stringify(data, null, 2);
                    } else {
                        displayText = String(data);
                    }
                } catch (parseError) {
                    console.log('ConstructQuery reply parse error:', parseError);
                    displayText = response?.data?.Data ?? String(parseError);
                    setResponseText(displayText);
                    setError(String(parseError));
                    return;
                }

                const normalizedData = normalizeSearchData(parsedData);
                const note = normalizedData?.note ?? null;

                setResponseText(displayText);
                if (onSubmit) onSubmit(payload, response);

                if (note) {
                    setNotePrompt({
                        note: String(note),
                        normalizedData
                    });
                    return;
                }

                runSearch(normalizedData);
            })
            .catch((err) => {
                console.log('ConstructQuery error:', err);
                const message = String(err);
                setResponseText(message);
                setError(message);
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const noteOpen = notePrompt !== null;

    return (
        <>
            <Dialog
                title="AI Search"
                isOpen={true}
                onClose={onHide}
                autoFocus={!noteOpen}
                canEscapeKeyClose={!submitting && !noteOpen}
                canOutsideClickClose={!submitting && !noteOpen}
                enforceFocus={!noteOpen}
                usePortal={true}
                className={Classes.DARK}
            >
                <div className={Classes.DIALOG_BODY}>
                    <FormGroup label="What are you looking for?" labelFor="brain-query">
                        <TextArea
                            id="brain-query"
                            fill
                            large
                            rows={4}
                            value={text}
                            placeholder="Describe what you are looking for..."
                            disabled={submitting || noteOpen}
                            onChange={(e) => setText(e.target.value)}
                        />
                    </FormGroup>
                    {keywords.length > 0 && (
                        <FormGroup label="Current keywords" helperText="These are already selected in the search bar">
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {keywords.map((keyword) => (
                                    <Tag key={keyword.id} minimal>
                                        {keyword.title || keyword.id}
                                    </Tag>
                                ))}
                            </div>
                        </FormGroup>
                    )}
                    {error && (
                        <Callout intent="danger" style={{ marginTop: 12 }}>
                            {error}
                        </Callout>
                    )}
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                    <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                        <Button onClick={onHide} disabled={submitting || noteOpen}>Cancel</Button>
                        <Button
                            intent="primary"
                            icon="predictive-analysis"
                            loading={submitting}
                            disabled={!text.trim() || submitting || noteOpen}
                            onClick={handleSearch}
                        >
                            Search
                        </Button>
                    </div>
                    {responseText !== null && (
                        <pre style={{
                            display: 'none',
                            marginTop: 12,
                            maxHeight: 240,
                            overflow: 'auto',
                            textAlign: 'left',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}>
                            {responseText}
                        </pre>
                    )}
                </div>
            </Dialog>
            <Alert
                className={Classes.DARK}
                confirmButtonText="OK"
                icon="info-sign"
                intent="primary"
                isOpen={noteOpen}
                canEscapeKeyClose={false}
                canOutsideClickClose={false}
                style={{ width: 560, maxWidth: '92vw' }}
                onConfirm={() => {
                    const { normalizedData } = notePrompt;
                    setNotePrompt(null);
                    runSearch(normalizedData);
                }}
            >
                <pre style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    lineHeight: 1.45
                }}>
                    {notePrompt?.note}
                </pre>
            </Alert>
        </>
    );
};
